/**
* @summary - License Certificate List
* @author Yuvasri S
* @since 2024-12-20
*/

import React, { useState, useEffect } from 'react';
import { Box, Button, Divider, Grid, IconButton, Stack, styled, Typography, useMediaQuery } from '@mui/material';
import CustomAgGrid from '../commoncomponents/CustomAgGrid';
import CustomDialog from '../commoncomponents/CustomDialog';
import { useLocation, useNavigate } from 'react-router-dom';
import { DefaultCard } from '../../assets/styled-components/card';
import CreateNewButton from '../commoncomponents/CreateNewButton';
import RefreshExportButtons from '../commoncomponents/RefreshExportButtons';
import CloseButton from '../commoncomponents/CloseButton';
import PageTitle from "../commoncomponents/PageTitle";
import SectionTitle from "../commoncomponents/SectionTitle";
import { dialogContent } from '../../utility/message';
import CustomSelectInput from '../commoncomponents/CustomSelectInput';
import LicenseCertificateRepository from '../../api/repositories/licenseCertificateRepository';
import dayjs from "dayjs";
import { ErrorChips, SuccessChips } from '../../assets/styled-components/chips';
import CustomTooltip from '../commoncomponents/CustomTooltip';
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";

function LicenseCertificateList() {

    const StyledCardTab = styled(Button)(({ theme }) => ({
        padding: "16px",
        flexDirection: "column",
        flex: 1,
        alignItems: "start",
        borderRadius: 0,
        gap: "4px",
        textTransform: "capitalize",
        "&.active": {
            backgroundColor: "#F6F2FA",
            "& .count": {
                color: theme.palette.primary.main,
            },
        },
        "&:hover": {
            "& .count": {
                color: theme.palette.primary.main,
            },
        },
    }));

    const location = useLocation();
    const [rows, setRows] = useState([]);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [gridApi, setGridApi] = useState(null);
    const [rowToDelete, setRowToDelete] = useState(null);
    const navigate = useNavigate();
    const [content, setContent] = useState();
    const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [activeTab, setActiveTab] = useState(location?.state?.tab || 0);
    const [allCount, setAllCount] = useState(0);
    const [activeCount, setActiveCount] = useState(0);
    const [exp6Count, setExp6Count] = useState(0);
    const [exp3Count, setExp3Count] = useState(0);
    const [exp1Count, setExp1Count] = useState(0);
    const [exp1wCount, setExp1wCount] = useState(0);
    const [inactiveCount, setInactiveCount] = useState(0);

    const serviceOptions1 = [
        { label: "FEMS", value: "FEMS", id: 1 },
        { label: "BEMS", value: "BEMS", id: 2 },
    ];
    const [service1, setService1] = useState(serviceOptions1[0]);

    const columns = [
        {
            field: 'licnno', headerName: 'License No', width: 150,
            cellRenderer: (params) => {
                if (params.data.isactiveformate !== 'Inactive') {
                    return (
                        <a
                            href="#"
                            onClick={(e) => {
                                e.preventDefault();
                                handleEdit(params.data);
                            }}
                            style={{ color: "blue", textDecoration: "underline", cursor: "pointer" }}
                        >
                            {params.value}
                        </a>
                    );
                } else {
                    return params.value || "";
                }
            }
        },
        { field: 'service', headerName: 'Service', width: 150 },
        { field: 'categoryName', headerName: 'License Category', width: 150 },
        { field: 'licntypecode', headerName: 'License Type Code', width: 150 },
        { field: 'licntypevalue', headerName: 'License Type Code Description', width: 150 },
        { field: 'licnclassgrade', headerName: 'Class/Grade', width: 150 },
        { field: 'licinspectiondateformate', headerName: 'Inspection Conducted on', width: 150 },
        { field: 'licnextinspectiondateformate', headerName: 'Next Inspection Date', width: 150 },
        { field: 'licnissuingbody', headerName: 'Issuing Body', width: 150 },
        { field: 'licnexpirydatestring', headerName: 'Expiry Date', width: 150 },
        {
            field: 'isactiveformate', headerName: 'Status', width: 150,
            cellRenderer: (params) => {
                if (params.value === 'Active') {
                    return <SuccessChips label={params.value} />;;
                } else if (params.value === 'Inactive') {
                    return <ErrorChips label={params.value} />;
                } else if (params.value === 'Expired') {
                    return <ErrorChips label={params.value} />;
                } else {
                    return null;
                }
            }
        },
        {
            field: 'actions', headerName: 'Actions', width: 150,
            cellRenderer: (params) => {
                return (
                    <>
                        {params.data.isactiveformate !== 'Inactive' && (
                            <CustomTooltip title="Edit">
                                <IconButton
                                    onClick={() => handleEdit(params.data)}
                                    data-testid="edit-button"
                                >
                                    <EditIcon />
                                </IconButton>
                            </CustomTooltip>
                        )}
                        <CustomTooltip title="View">
                            <IconButton
                                onClick={() => handleView(params.data)}
                                data-testid="view-button"
                            >
                                <VisibilityIcon />
                            </IconButton>
                        </CustomTooltip>
                        {(localStorage.getItem('roleName') === 'CorporateAdmin' || localStorage.getItem('roleName') === 'HospitalFM') && (
                            <CustomTooltip title="Delete">
                                <IconButton
                                    onClick={() => handleDelete(params.data)}
                                    data-testid="delete-button"
                                >
                                    <DeleteIcon />
                                </IconButton>
                            </CustomTooltip>
                        )}
                    </>
                )
            }
        }
    ];

    const fetchCalibrationCertificate = async () => {
        setIsLoading(true);
        try {
            const response = await LicenseCertificateRepository.licenseCertificateList(activeTab);
            // const transformedRows = response?.data?.map(row => ({
            //     ...row,
            //     licnexpirydateformat: row.licnexpirydatestring
            //         ? dayjs(row.licnexpirydatestring, 'DD/MM/YYYY').format('YYYY-MM-DDTHH:mm:ss')
            //         : 'N/A',
            // }));
            setRows(response?.data);
            //    setFilteredRows(response.data);
        } catch (error) {
            // Handle errors
        } finally {
            setIsLoading(false);
        }
    };

    const fetchCalibrationCertificateCount = async () => {
        setIsLoading(true);
        try {
            const response = await LicenseCertificateRepository.licenseCertificateListcount();

            setAllCount(response?.data[0]?.allCount);
            setActiveCount(response?.data[0]?.activeCount);
            setExp6Count(response?.data[0]?.license6Month);
            setExp3Count(response?.data[0]?.license3Month);
            setExp1Count(response?.data[0]?.license1Month);
            setExp1wCount(response?.data[0]?.license1Week);
            setInactiveCount(response?.data[0]?.inactiveLicense);

        } catch (error) {
            // Handle errors
        } finally {
            setIsLoading(false);
        }
    };


    const deleteCalibrateCertificate = async (id) => {
        try {
            const response = await LicenseCertificateRepository.deleteLicenseCertificate(id);
            if (response.status === 204) {
                setConfirmationDialogOpen(true)
                setContent('Record deleted successfully.')
            }
            fetchCalibrationCertificate();
        }
        catch (error) {

            if (error.response && error.response.status === 400) {
                setConfirmationDialogOpen(true);

                setContent(error ? error.response.data.detail : "Error Occured!")
            }
        }
    };

    const handleRefresh = (tabIndex) => {
        fetchCalibrationCertificate(tabIndex);
        fetchCalibrationCertificateCount();
    };


    const handleCreateLicenseCertificate = () => {
        navigate("register", { state: { title: 'Create License Certificate Details' } });
    };

    const handleEdit = (rowData) => {
        navigate("register", { state: { ...rowData, editmode: true, title: 'Edit License Certificate Details' } });
    };

    const handleView = (rowData) => {
        navigate("register", { state: { ...rowData, viewmode: true, title: 'View License Certificate Details' } });
    };

    const confirmDelete = async () => {
        if (rowToDelete) {
            await deleteCalibrateCertificate(rowToDelete.id);
            setDeleteDialogOpen(false);
            setConfirmationDialogOpen(true);
            setRowToDelete(null);
        }
    };
    const handleDelete = (row) => {
        setRowToDelete(row);
        setDeleteDialogOpen(true);
    };

    const handleTabChange = (tabIndex) => {
        setActiveTab(tabIndex);  // Update the active tab
        //  fetchCalibrationCertificate(tabIndex); // Fetch data for the selected tab
    };

    useEffect(() => {
        fetchCalibrationCertificate();
        fetchCalibrationCertificateCount();
    }, [activeTab]);

    return (
        <>
            <PageTitle title="License Certificate Details" />

            <Box mt={{ xs: 1, sm: 2 }}>
                <DefaultCard>
                    <Grid container spacing={2}>
                        <Grid item xs={13} md={9}>
                            <Stack
                                direction={"row"}
                                divider={<Divider orientation="vertical" flexItem />}
                                border={0.5}
                                borderColor={"divider"}
                                borderRadius={4}
                                overflow={"hidden"}
                                height={"100%"}
                            >
                                <StyledCardTab
                                    className={`${activeTab === 0 ? "active" : null}`}
                                    onClick={() => handleTabChange(0)}

                                >
                                    <Typography color={"text.primary"} fontSize={13} fontWeight={900}>
                                        All License
                                    </Typography>
                                    <Typography
                                        className="count"
                                        color={"text.secondary"}
                                        fontSize={24}
                                        fontWeight={500}
                                    >
                                        {allCount}
                                    </Typography>
                                </StyledCardTab>
                                <StyledCardTab
                                    className={`${activeTab === 1 ? "active" : null}`}
                                    onClick={() => handleTabChange(1)}
                                >
                                    <Typography color={"text.primary"} fontSize={13} fontWeight={900}>
                                        Active License
                                    </Typography>
                                    <Typography
                                        className="count"
                                        color={"text.secondary"}
                                        fontSize={24}
                                        fontWeight={500}
                                    >
                                        {activeCount}
                                    </Typography>
                                </StyledCardTab>
                                <StyledCardTab
                                    className={`${activeTab === 2 ? "active" : null}`}
                                    onClick={() => handleTabChange(2)}
                                >
                                    <Typography color={"text.primary"} fontSize={13} fontWeight={900}>
                                        Expiring within 6 Months
                                    </Typography>
                                    <Typography
                                        className="count"
                                        color={"text.secondary"}
                                        fontSize={24}
                                        fontWeight={500}
                                    >
                                        {exp6Count}
                                    </Typography>
                                </StyledCardTab>
                                <StyledCardTab
                                    className={`${activeTab === 3 ? "active" : null}`}
                                    onClick={() => handleTabChange(3)}
                                >
                                    <Typography color={"text.primary"} fontSize={13} fontWeight={900}>
                                        Expiring within 3 Months
                                    </Typography>
                                    <Typography
                                        className="count"
                                        color={"text.secondary"}
                                        fontSize={24}
                                        fontWeight={500}
                                    >
                                        {exp3Count}
                                    </Typography>
                                </StyledCardTab>
                                <StyledCardTab
                                    className={`${activeTab === 4 ? "active" : null}`}
                                    onClick={() => handleTabChange(4)}
                                >
                                    <Typography color={"text.primary"} fontSize={13} fontWeight={900}>
                                        Expiring within 1 Month
                                    </Typography>
                                    <Typography
                                        className="count"
                                        color={"text.secondary"}
                                        fontSize={24}
                                        fontWeight={500}
                                    >
                                        {exp1Count}
                                    </Typography>
                                </StyledCardTab>

                                <StyledCardTab
                                    className={`${activeTab === 5 ? "active" : null}`}
                                    onClick={() => handleTabChange(5)}
                                >
                                    <Typography color={"text.primary"} fontSize={13} fontWeight={900}>
                                        Expiring within 1 Week
                                    </Typography>
                                    <Typography
                                        className="count"
                                        color={"text.secondary"}
                                        fontSize={24}
                                        fontWeight={500}
                                    >
                                        {exp1wCount}
                                    </Typography>
                                </StyledCardTab>
                                <StyledCardTab
                                    className={`${activeTab === 6 ? "active" : null}`}
                                    onClick={() => handleTabChange(6)}
                                >
                                    <Typography color={"text.primary"} fontSize={13} fontWeight={900}>
                                        Expired License
                                    </Typography>
                                    <Typography
                                        className="count"
                                        color={"text.secondary"}
                                        fontSize={24}
                                        fontWeight={500}
                                    >
                                        {inactiveCount}
                                    </Typography>
                                </StyledCardTab>

                            </Stack>
                        </Grid>
                    </Grid>
                </DefaultCard>
            </Box>
            <DefaultCard>
                <Box
                    mb={1}
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-end",
                    }}
                >
                    <SectionTitle title="License Certificate Details List" />
                    <Stack direction={"row"} alignItems={"center"} gap={1}>

                        <CreateNewButton onClick={handleCreateLicenseCertificate} />
                        <RefreshExportButtons
                            pagination={false}
                            exportFunction={() => { }}
                            exportData={rows}
                            refData={gridApi}
                            handleRefresh={handleRefresh}
                            title="License Certificate Details"
                        />
                    </Stack>
                </Box>
                <CustomAgGrid
                    rows={rows}
                    columns={columns}
                    // handleEdit={handleEdit}
                    // handleDelete={handleDelete}
                    // handleView={handleView}
                    setGridApi={setGridApi}
                    isLoading={isLoading}
                    ref={gridApi}
                    pagination={true}
                />
                <CustomDialog
                    open={deleteDialogOpen}
                    onClose={() => setDeleteDialogOpen(false)}

                    onConfirm={confirmDelete}
                    content={dialogContent.confirmDelete}
                    cancelBtn='No'
                    confirmBtn="Yes"
                />
                <CustomDialog
                    open={confirmationDialogOpen}
                    onClose={() => setConfirmationDialogOpen(false)}
                    content={
                        content
                    }
                    confirmBtn="OK"
                    onConfirm={() => setConfirmationDialogOpen(false)}
                />

            </DefaultCard>
            <CloseButton />

        </>
    );
}

export default LicenseCertificateList;